import "./../../assets/css/admin/AdminHeader.css";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// ── PAGE META MAP — add new routes here to get automatic title/subtitle ──────
const PAGE_META = {
  "/admin/dashboard": { title: "Dashboard", subtitle: "Overview & Analytics" },
  "/admin/sub-admin": {
    title: "Sub Admin",
    subtitle: "Manage sub administrators",
  },
  "/admin/students": {
    title: "Students Management",
    subtitle: "Manage platform students",
  },
  "/admin/mentors": {
    title: "Mentor Management",
    subtitle: "Manage platform mentors",
  },
  "/admin/roadmaps": {
    title: "Roadmaps",
    subtitle: "Manage learning roadmaps",
  },
  "/admin/courses": { title: "Courses", subtitle: "Manage course catalog" },
  "/admin/categories": {
    title: "Categories",
    subtitle: "Manage course categories",
  },
  "/admin/mentors/verification": {
    title: "Mentors Verification",
    subtitle: "Review mentor applications",
  },
  "/admin/ai-tutor": {
    title: "AI Tutor",
    subtitle: "AI assistant configuration",
  },
  "/admin/analytics": {
    title: "Analytics",
    subtitle: "Platform insights & reports",
  },
  "/admin/reports": {
    title: "Reports & Disputes",
    subtitle: "Manage reports and disputes",
  },
  "/admin/settings": {
    title: "System Settings",
    subtitle: "Configure platform settings",
  },
  "/admin/profile": { title: "My Profile", subtitle: "Account information" },
  "/admin/notifications": {
    title: "Notifications",
    subtitle: "All notifications",
  },
  "/admin/earnings": {
    title: "Earnings Management",
    subtitle: "Overview of platform earnings",
  },
  "/admin/withdrawals": {
    title: "Withdrawals",
    subtitle: "Manage withdrawal requests",
  },
  "/admin/chat": { title: "Chat", subtitle: "Messages & conversations" },
};

const AdminHeader = ({
  theme,
  toggleTheme,
  isSidebarCollapsed,
  toggleSidebar,
  mode,
  pageTitle,
  pageSubtitle,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const resolvedMeta = PAGE_META[location.pathname] || {};
  const displayTitle = pageTitle || resolvedMeta.title || "Tech Mentor";
  const displaySubtitle =
    pageSubtitle || resolvedMeta.subtitle || "ADMIN PANEL";

  const buildFullName = () => {
    const first = localStorage.getItem("firstname") || "";
    const last = localStorage.getItem("lastname") || "";
    const full = (first + " " + last).trim();
    return full || "Admin";
  };

  const [userData, setUserData] = useState({
    name: buildFullName(),
    email: localStorage.getItem("email") || "admin@techmentor.com",
    profileImage: null,
  });

  const handleLogout = () => {
    localStorage.clear();
    navigate("/admin/login");
  };

  useEffect(() => {
    setUserData((prev) => ({
      ...prev,
      name: buildFullName(),
      email: localStorage.getItem("email") || prev.email,
    }));
    const img = localStorage.getItem("as-profile-image");
    if (img) setUserData((prev) => ({ ...prev, profileImage: img }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const notificationsData = [
    {
      id: 1,
      text: "New user registration pending approval",
      time: "2 min ago",
      type: "info",
    },
    {
      id: 2,
      text: "System backup completed successfully",
      time: "15 min ago",
      type: "success",
    },
    {
      id: 3,
      text: "Server CPU usage exceeded 80%",
      time: "1 hour ago",
      type: "warning",
    },
    {
      id: 4,
      text: "Monthly report is ready for review",
      time: "3 hours ago",
      type: "info",
    },
  ];

  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [notificationCount] = useState(notificationsData.length);

  const profileDropdownRef = useRef(null);
  const notificationPanelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(e.target)
      )
        setProfileDropdownOpen(false);
      if (
        notificationPanelRef.current &&
        !notificationPanelRef.current.contains(e.target)
      )
        setNotificationPanelOpen(false);
    };
    if (profileDropdownOpen || notificationPanelOpen)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profileDropdownOpen, notificationPanelOpen]);

  const getUserInitial = () => userData.name.charAt(0).toUpperCase();
  const getNotificationDotClass = (type) =>
    `ah-notification-dot ah-notification-dot-${type}`;

  return (
    <header className="ah-header">
      <div className="ah-container">
        {/* LEFT — Hamburger + Page Title */}
        <div className="ah-left">
          {mode !== "desktop" && (
            <button
              className="ah-hamburger"
              onClick={toggleSidebar}
              aria-label="Toggle sidebar"
            >
              <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
                <path
                  d="M3 6H21M3 12H21M3 18H21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          <div className="ah-brand">
            <h1 className="ah-product-name">{displayTitle}</h1>
            <span className="ah-subtitle">{displaySubtitle}</span>
          </div>
        </div>

        {/* CENTER — Search */}
        <div className="ah-center">
          <div className="ah-search-wrapper">
            <svg className="ah-search-icon" viewBox="0 0 20 20" fill="none">
              <path
                d="M9 2C5.134 2 2 5.134 2 9C2 12.866 5.134 16 9 16C10.663 16 12.191 15.42 13.393 14.452L16.293 17.351C16.683 17.742 17.317 17.742 17.707 17.351C18.098 16.961 18.098 16.328 17.707 15.937L14.808 13.038C15.776 11.835 16.356 10.307 16.356 8.644C16.356 4.778 13.222 1.644 9.356 1.644L9 2ZM9 4C11.761 4 14 6.239 14 9C14 11.761 11.761 14 9 14C6.239 14 4 11.761 4 9C4 6.239 6.239 4 9 4Z"
                fill="currentColor"
              />
            </svg>
            <input
              type="text"
              className="ah-search-input"
              placeholder="Search for data, users, or reports..."
              aria-label="Search dashboard"
            />
          </div>
        </div>

        {/* RIGHT — Chat, Notifications, Theme, Profile */}
        <div className="ah-right">

          {/* Chat */}
          <button
            className="ah-icon-btn ah-chat-btn"
            onClick={() => navigate("/admin/chat")}
            aria-label="Open chat"
          >
            <svg className="ah-icon" viewBox="0 0 26 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Back bubble — secondary/muted */}
              <path
                d="M20.5 7.5C23.538 7.5 26 9.462 26 11.875C26 14.288 23.538 16.25 20.5 16.25H19.8L18.5 19L17.2 16.25C15.6 16.25 14.3 15.6 13.5 14.6C16.2 14.05 18.2 12.15 18.2 9.875C18.2 9.25 18.06 8.65 17.82 8.1C18.67 7.72 19.57 7.5 20.5 7.5Z"
                fill="currentColor"
                opacity="0.3"
              />
              {/* Front bubble — solid */}
              <path
                d="M11 0C5.477 0 1 3.358 1 7.5C1 9.838 2.324 11.93 4.45 13.3L3 17L7.2 15.1C8.42 15.68 9.68 16 11 16C16.523 16 21 12.642 21 8.5C21 4.358 16.523 0 11 0Z"
                fill="currentColor"
              />
              {/* Lines inside front bubble */}
              <line x1="7" y1="7" x2="15" y2="7" stroke="var(--ah-bg-primary)" strokeWidth="1.7" strokeLinecap="round"/>
              <line x1="7" y1="10.5" x2="13" y2="10.5" stroke="var(--ah-bg-primary)" strokeWidth="1.7" strokeLinecap="round"/>
            </svg>
            <span className="ah-badge ah-chat-badge">3</span>
          </button>

          {/* Notifications */}
          <div className="ah-notification-wrapper" ref={notificationPanelRef}>
            <button
              className="ah-icon-btn ah-notification-btn"
              onClick={() => {
                setProfileDropdownOpen(false);
                setNotificationPanelOpen((p) => !p);
              }}
              aria-label="Notifications"
              aria-expanded={notificationPanelOpen}
            >
              <svg className="ah-icon" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2C11.448 2 11 2.448 11 3V3.563C8.661 4.09 7 6.187 7 8.7V13.732L5.539 16.655C5.356 16.993 5.356 17.408 5.539 17.745C5.721 18.082 6.064 18.3 6.439 18.3H9.226C9.441 19.813 10.707 21 12.25 21C13.793 21 15.059 19.813 15.274 18.3H17.561C17.936 18.3 18.279 18.082 18.461 17.745C18.644 17.408 18.644 16.993 18.461 16.655L17 13.732V8.7C17 6.187 15.339 4.09 13 3.563V3C13 2.448 12.552 2 12 2ZM12 5C13.657 5 15 6.343 15 8V14L16.369 16.7H7.631L9 14V8C9 6.343 10.343 5 12 5ZM11.25 18.3H13.25C13.112 18.946 12.552 19.4 11.875 19.4C11.198 19.4 10.639 18.946 10.5 18.3H11.25Z"
                  fill="currentColor"
                />
              </svg>
              {notificationCount > 0 && (
                <span className="ah-badge">{notificationCount}</span>
              )}
            </button>

            {notificationPanelOpen && (
              <div
                className="ah-notification-panel"
                role="dialog"
                aria-label="Notifications"
              >
                <div className="ah-notification-header">
                  <h3 className="ah-notification-title">Notifications</h3>
                  <span className="ah-notification-count">
                    {notificationCount} new
                  </span>
                </div>
                <div className="ah-notification-list">
                  {notificationsData.map((n) => (
                    <div key={n.id} className="ah-notification-item">
                      <div className={getNotificationDotClass(n.type)} />
                      <div className="ah-notification-content">
                        <p className="ah-notification-text">{n.text}</p>
                        <span className="ah-notification-time">{n.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="ah-notification-footer">
                  <button
                    className="ah-notification-view-all"
                    onClick={() => navigate("/admin/notifications")}
                  >
                    View all notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          <button
            className="ah-icon-btn ah-theme-toggle"
            onClick={toggleTheme}
            aria-label={
              theme === "dark" ? "Switch to light mode" : "Switch to dark mode"
            }
          >
            {theme === "dark" ? (
              <svg
                className="ah-icon ah-theme-icon"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle cx="12" cy="12" r="4" fill="currentColor" />
                <path
                  d="M12 2V4M12 20V22M22 12H20M4 12H2M19.071 4.929L17.657 6.343M6.343 17.657L4.929 19.071M19.071 19.071L17.657 17.657M6.343 6.343L4.929 4.929"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg
                className="ah-icon ah-theme-icon"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M21.529 15.929C21.369 15.659 21.039 15.539 20.739 15.649C19.799 15.999 18.799 16.179 17.779 16.179C13.189 16.179 9.479 12.469 9.479 7.879C9.479 6.219 10.019 4.659 11.009 3.389C11.199 3.139 11.209 2.799 11.029 2.539C10.849 2.279 10.519 2.159 10.209 2.239C5.519 3.569 2.319 7.979 2.319 12.919C2.319 19.009 7.269 23.959 13.359 23.959C17.709 23.959 21.579 21.559 23.329 17.939C23.459 17.659 23.389 17.329 23.169 17.119C22.949 16.909 22.619 16.859 22.349 17.009L21.529 15.929Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>

          {/* Profile */}
          <div className="ah-profile-wrapper" ref={profileDropdownRef}>
            <button
              className="ah-profile-btn"
              onClick={() => {
                setNotificationPanelOpen(false);
                setProfileDropdownOpen((p) => !p);
              }}
              aria-expanded={profileDropdownOpen}
              aria-label="User profile menu"
            >
              <div className="ah-avatar">
                {userData.profileImage ? (
                  <img
                    src={userData.profileImage}
                    alt={userData.name}
                    className="ah-avatar-image"
                  />
                ) : (
                  <div className="ah-avatar-letter">{getUserInitial()}</div>
                )}
              </div>
            </button>

            {profileDropdownOpen && (
              <div className="ah-dropdown" role="menu">
                <div className="ah-dropdown-user-info">
                  <div className="ah-dropdown-user-name">{userData.name}</div>
                  <div className="ah-dropdown-user-email">{userData.email}</div>
                </div>
                <div className="ah-dropdown-divider" />
                <button
                  className="ah-dropdown-item"
                  role="menuitem"
                  onClick={() => {
                    setProfileDropdownOpen(false);
                    navigate("/admin/profile");
                  }}
                >
                  <svg
                    className="ah-dropdown-icon"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M10 2C7.791 2 6 3.791 6 6C6 8.209 7.791 10 10 10C12.209 10 14 8.209 14 6C14 3.791 12.209 2 10 2Z"
                      fill="currentColor"
                    />
                    <path
                      d="M4 16C4 13.791 5.791 12 8 12H12C14.209 12 16 13.791 16 16V17C16 17.552 15.552 18 15 18H5C4.448 18 4 17.552 4 17V16Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span>My Profile</span>
                </button>
                <button
                  className="ah-dropdown-item"
                  role="menuitem"
                  onClick={() => {
                    setProfileDropdownOpen(false);
                    navigate("/admin/settings");
                  }}
                >
                  <svg
                    className="ah-dropdown-icon"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M10 2C8.895 2 8 2.895 8 4V5H5C4.448 5 4 5.448 4 6C4 6.552 4.448 7 5 7H5.171L5.879 16.121C5.946 17.162 6.804 18 7.848 18H12.152C13.196 18 14.054 17.162 14.121 16.121L14.829 7H15C15.552 7 16 6.552 16 6C16 5.448 15.552 5 15 5H12V4C12 2.895 11.105 2 10 2Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span>Settings</span>
                </button>
                <div className="ah-dropdown-divider" />
                <button
                  className="ah-dropdown-item ah-logout"
                  role="menuitem"
                  onClick={handleLogout}
                >
                  <svg
                    className="ah-dropdown-icon"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M3 3C2.448 3 2 3.448 2 4V16C2 16.552 2.448 17 3 17H10C10.552 17 11 16.552 11 16C11 15.448 10.552 15 10 15H4V5H10C10.552 5 11 4.552 11 4C11 3.448 10.552 3 10 3H3Z"
                      fill="currentColor"
                    />
                    <path
                      d="M14.293 7.293C13.902 7.683 13.902 8.317 14.293 8.707L15.586 10L8 10C7.448 10 7 10.448 7 11C7 11.552 7.448 12 8 12L15.586 12L14.293 13.293C13.902 13.683 13.902 14.317 14.293 14.707C14.683 15.098 15.317 15.098 15.707 14.707L18.707 11.707C19.098 11.317 19.098 10.683 18.707 10.293L15.707 7.293C15.317 6.902 14.683 6.902 14.293 7.293Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;